import React, { useMemo, useState } from 'react';
import { formatDateTime } from './adminFormat';
import { LOAD_METRICS, SCENARIOS, VIEWS } from './mapPerfRun';

// The Map speed tab's graphs (MapPerfTab.js) and the helpers that compare a run with the runs before it.

export const BASELINE_RUNS = 5;
// A change counts once it is this much worse or better than the median of the earlier runs, and by more than the
// measure's own noise floor.
export const SIGNIFICANT = 0.15;
export const METRICS = [
    { id: 'avg', label: 'Avg frame', unit: 'ms', floor: 0.5 },
    { id: 'p95', label: 'p95', unit: 'ms', floor: 1 },
    { id: 'p99', label: 'p99', unit: 'ms', floor: 2 },
    { id: 'fps', label: 'fps', higher: true, floor: 2 },
    { id: 'slow', label: '> 50 ms', floor: 2, int: true },
    { id: 'longTaskMs', label: 'Long tasks', unit: 'ms', floor: 50 },
];
const SMOOTH_METRIC = { id: 'smoothPct', label: 'Smooth frames', unit: '%', higher: true, floor: 2 };
export const LOAD_FLOOR = 300;

const median = (values) => {
    const sorted = values.filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
    if (!sorted.length) return null;
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

// A scenario's measure; `smoothPct` (the share of frames on time) is worked out from the counts.
export const valueOf = (run, view, scenario, metric) => {
    const result = run?.results?.[view]?.[scenario];
    if (!result) return undefined;
    if (metric === 'smoothPct') return Number.isFinite(result.smooth) && result.frames ? (100 * result.smooth) / result.frames : undefined;
    return result[metric];
};

export const runTime = (run) => String(run.at || run.created_at || '');

// The earlier valid runs on the run's device, newest last, at most BASELINE_RUNS.
export const baselineRuns = (run, runs) => runs
    .filter((other) => other !== run && !other.invalid && other.device?.key === run.device?.key && runTime(other) < runTime(run))
    .slice(-BASELINE_RUNS);

// { ratio, base, direction: 'worse' | 'better' | null } for a value against its baseline values.
export const compare = (value, baseValues, { higher = false, floor = 0 } = {}) => {
    const base = median(baseValues);
    if (!Number.isFinite(value) || base === null || base === 0) return null;
    const ratio = (value - base) / base;
    const significant = Math.abs(ratio) >= SIGNIFICANT && Math.abs(value - base) > floor;
    const worse = higher ? ratio < 0 : ratio > 0;
    return { ratio, base, direction: significant ? (worse ? 'worse' : 'better') : null };
};

export const num = (value, unit, int = false) => {
    if (!Number.isFinite(value)) return '—';
    let text;
    if (int || value === 0 || value >= 100) text = Math.round(value).toLocaleString('sv-SE');
    else text = value.toFixed(value >= 10 ? 1 : 2);
    return unit ? `${text} ${unit}` : text;
};

const seconds = (ms) => (Number.isFinite(ms) ? `${(ms / 1000).toFixed(ms < 10000 ? 2 : 1)} s` : '—');
const percent = (part) => `${part >= 99.5 || part === 0 ? Math.round(part) : part.toFixed(1)} %`;

export const shortCommit = (run) => (run.build?.commit || (run.env === 'dev' ? 'dev' : '—'));

export const Delta = ({ result }) => {
    if (!result) return null;
    const sign = result.ratio > 0 ? '+' : '';
    return (
        <span className={`ax-mp-delta${result.direction ? ` is-${result.direction}` : ''}`} title={`Earlier runs: ${num(result.base)}`}>
            {sign}{Math.round(result.ratio * 100)} %
        </span>
    );
};

// ---------- this run ----------

// How smooth each scenario was: every frame drawn, as one bar — on time, a little late, a stutter — and the fps
// against what the screen can show.
export const SmoothnessChart = ({ run, baseline }) => {
    const hz = run.device?.hz || 60;
    return (
        <div className="ax-mpc">
            <div className="ax-mpc-views">
                {VIEWS.map((view) => (
                    <div key={view.id} className="ax-mpc-view">
                        <h4>{view.label}</h4>
                        {SCENARIOS.map((scenario) => {
                            const result = run.results?.[view.id]?.[scenario.id];
                            if (!result) return null;
                            const frames = result.frames || 1;
                            const counted = Number.isFinite(result.smooth);
                            const parts = counted
                                ? [
                                    { key: 'smooth', share: (100 * result.smooth) / frames, label: 'on time' },
                                    { key: 'hitch', share: (100 * result.hitch) / frames, label: 'a little late' },
                                    { key: 'stutter', share: (100 * result.slow) / frames, label: 'stutters (over 50 ms)' },
                                ]
                                : [{ key: 'fps', share: Math.min(100, (100 * result.fps) / hz), label: `of the screen's ${hz} fps` }];
                            const fpsChange = compare(result.fps, baseline.map((other) => valueOf(other, view.id, scenario.id, 'fps')), METRICS[3]);
                            return (
                                <div key={scenario.id} className="ax-mpc-row">
                                    <span className="ax-mpc-label" title={scenario.hint}>{scenario.label}</span>
                                    <span
                                        className="ax-mpc-bar"
                                        title={parts.map((part) => `${percent(part.share)} ${part.label}`).join(' · ')}
                                    >
                                        {parts.map((part) => (
                                            <i key={part.key} className={`is-${part.key}`} style={{ width: `${part.share}%` }} />
                                        ))}
                                    </span>
                                    <span className="ax-mpc-value">
                                        <b>{Math.round(result.fps)}</b> fps
                                        <Delta result={fpsChange} />
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
            <div className="ax-mpc-key">
                <span className="is-smooth">On time</span>
                <span className="is-hitch">A little late</span>
                <span className="is-stutter">Stutter (over 50 ms)</span>
                <span className="ax-muted">The screen shows up to {hz} fps.</span>
            </div>
        </div>
    );
};

// Each scenario's frame time against the runs before: left is faster, right slower, the grey band the usual
// variation between runs.
const CHANGE_RANGE = 0.5;
export const ChangeChart = ({ run, baseline }) => {
    const rows = VIEWS.flatMap((view) => SCENARIOS.map((scenario) => {
        const value = valueOf(run, view.id, scenario.id, 'avg');
        const result = compare(value, baseline.map((other) => valueOf(other, view.id, scenario.id, 'avg')), METRICS[0]);
        return { view, scenario, result };
    })).filter((row) => row.result);
    if (!rows.length) return null;
    const pos = (ratio) => 50 + (Math.max(-CHANGE_RANGE, Math.min(CHANGE_RANGE, ratio)) / CHANGE_RANGE) * 50;
    const band = (SIGNIFICANT / CHANGE_RANGE) * 50;
    return (
        <div className="ax-mpc">
            <div className="ax-mpc-change-head">
                <span>← faster</span>
                <span>average frame time against the {baseline.length > 1 ? `${baseline.length} runs` : 'run'} before</span>
                <span>slower →</span>
            </div>
            {rows.map(({ view, scenario, result }, index) => {
                const start = Math.min(50, pos(result.ratio));
                const width = Math.abs(pos(result.ratio) - 50);
                const beyond = Math.abs(result.ratio) > CHANGE_RANGE;
                return (
                    <div key={`${view.id}-${scenario.id}`} className={`ax-mpc-row${index % SCENARIOS.length === 0 ? ' is-first' : ''}`}>
                        <span className="ax-mpc-label">
                            <span className="ax-mpc-view-name">{index % SCENARIOS.length === 0 ? view.label : ''}</span>
                            {scenario.label}
                        </span>
                        <span className="ax-mpc-track">
                            <span className="ax-mpc-band" style={{ left: `${50 - band}%`, width: `${band * 2}%` }} />
                            <span className="ax-mpc-zero" />
                            <span
                                className={`ax-mpc-change${result.direction ? ` is-${result.direction}` : ''}`}
                                style={{ left: `${start}%`, width: `${Math.max(0.6, width)}%` }}
                            />
                        </span>
                        <span className={`ax-mpc-value${result.direction ? ` is-${result.direction}` : ''}`}>
                            {result.ratio > 0 ? '+' : ''}{Math.round(result.ratio * 100)} %{beyond ? ' ›' : ''}
                        </span>
                    </div>
                );
            })}
            <p className="ax-mpc-note ax-muted">
                The grey band is ±{Math.round(SIGNIFICANT * 100)} %: changes inside it are usually just variation between runs.
            </p>
        </div>
    );
};

// How each view came up: the map, then the areas' colours, then the homes, on one time line.
export const LoadTimeline = ({ run, baseline }) => {
    const ends = VIEWS.map((view) => run.results?.[view.id]?.load?.homesMs).filter(Number.isFinite);
    if (!ends.length) return null;
    const scale = Math.ceil(Math.max(...ends) / 1000) * 1000 || 1000;
    return (
        <div className="ax-mpc">
            {VIEWS.map((view) => {
                const load = run.results?.[view.id]?.load;
                if (!load) return null;
                let from = 0;
                return (
                    <div key={view.id} className="ax-mpc-load">
                        <span className="ax-mpc-label">{view.label}</span>
                        <span className="ax-mpc-timeline">
                            {LOAD_METRICS.map((metric, index) => {
                                const to = load[metric.id];
                                const left = (100 * from) / scale;
                                const width = (100 * Math.max(0, to - from)) / scale;
                                from = Math.max(from, to);
                                return <i key={metric.id} className={`is-step-${index}`} style={{ left: `${left}%`, width: `${width}%` }} title={`${metric.label}: ${seconds(to)}`} />;
                            })}
                        </span>
                        <span className="ax-mpc-load-steps">
                            {LOAD_METRICS.map((metric, index) => (
                                <span key={metric.id} className={`is-step-${index}`}>
                                    {metric.label} <b>{seconds(load[metric.id])}</b>
                                    <Delta result={compare(load[metric.id], baseline.map((other) => other.results?.[view.id]?.load?.[metric.id]), { floor: LOAD_FLOOR })} />
                                </span>
                            ))}
                        </span>
                    </div>
                );
            })}
            <div className="ax-mpc-scale"><span>0 s</span><span>{seconds(scale)}</span></div>
        </div>
    );
};

// ---------- over time ----------

export const TREND_METRICS = [
    { id: 'fps', label: 'fps', hint: 'frames a second; higher is better', higher: true },
    { id: 'smoothPct', label: 'Smooth frames', hint: 'share of frames on time; higher is better', unit: '%', higher: true },
    { id: 'avg', label: 'Avg frame', hint: 'average time per frame; lower is better', unit: 'ms' },
    { id: 'p95', label: 'p95', hint: 'the slowest 5 % of frames take at least this; lower is better', unit: 'ms' },
    { id: 'longTaskMs', label: 'Long tasks', hint: 'time the page was busy in tasks over 50 ms; lower is better', unit: 'ms' },
];

const CHART_W = 300;
const CHART_H = 130;
const PAD = { left: 34, right: 10, top: 10, bottom: 16 };

const metricInfo = (id) => (id === 'smoothPct' ? SMOOTH_METRIC : METRICS.find((metric) => metric.id === id)) || {};

// One small chart: a line per view over the runs, the hovered run marked in every chart at once (`hover`), and a
// red ring on a run that was clearly worse than the runs before it.
const SmallChart = ({ title, runs, allRuns, read, metric, hover, onHover, max, ceiling }) => {
    const x = (index) => PAD.left + (runs.length > 1 ? (index / (runs.length - 1)) * (CHART_W - PAD.left - PAD.right) : (CHART_W - PAD.left - PAD.right) / 2);
    const y = (value) => PAD.top + (1 - value / max) * (CHART_H - PAD.top - PAD.bottom);
    const info = metric.id === 'homesS' ? { floor: LOAD_FLOOR / 1000 } : metricInfo(metric.id);
    const shown = hover ?? runs.length - 1;
    const onMove = (event) => {
        const box = event.currentTarget.getBoundingClientRect();
        const px = ((event.clientX - box.left) / box.width) * CHART_W;
        let best = 0;
        runs.forEach((_, index) => { if (Math.abs(x(index) - px) < Math.abs(x(best) - px)) best = index; });
        onHover(best);
    };
    return (
        <div className="ax-mpc-small">
            <div className="ax-mpc-small-head">
                <span>{title}</span>
                <span className="ax-mpc-small-values">
                    {VIEWS.map((view) => (
                        <span key={view.id} className={`is-${view.id}`}>{num(read(runs[shown], view.id))}</span>
                    ))}
                </span>
            </div>
            <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} onMouseMove={onMove} onMouseLeave={() => onHover(null)} role="img" aria-label={`${title} over the runs`}>
                {[0, max / 2, max].map((tick) => (
                    <g key={tick}>
                        <line x1={PAD.left} x2={CHART_W - PAD.right} y1={y(tick)} y2={y(tick)} className="ax-mp-grid" />
                        <text x={PAD.left - 5} y={y(tick) + 3} textAnchor="end" className="ax-mp-tick">{num(tick, '', tick >= 10)}</text>
                    </g>
                ))}
                {ceiling && (
                    <g>
                        <line x1={PAD.left} x2={CHART_W - PAD.right} y1={y(ceiling)} y2={y(ceiling)} className="ax-mpc-ceiling" />
                        <text x={PAD.left + 3} y={y(ceiling) - 4} className="ax-mp-tick">screen max</text>
                    </g>
                )}
                {hover !== null && hover !== undefined && <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={CHART_H - PAD.bottom} className="ax-mpc-cross" />}
                {VIEWS.map((view) => {
                    const points = runs.map((run, index) => ({ run, index, value: read(run, view.id) })).filter((point) => Number.isFinite(point.value));
                    return (
                        <g key={view.id} className={`ax-mp-line is-${view.id}`}>
                            {points.length > 1 && <polyline points={points.map((point) => `${x(point.index)},${y(point.value)}`).join(' ')} />}
                            {points.map((point) => {
                                const worse = compare(point.value, baselineRuns(point.run, allRuns).map((other) => read(other, view.id)), info)?.direction === 'worse';
                                const big = point.index === shown;
                                return (
                                    <circle
                                        key={point.index}
                                        cx={x(point.index)}
                                        cy={y(point.value)}
                                        r={big ? 4.5 : 3}
                                        className={`${worse ? 'is-worse' : ''}${point.run.invalid ? ' is-invalid' : ''}`}
                                    />
                                );
                            })}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

export const TrendGrid = ({ runs, allRuns }) => {
    const [metricId, setMetricId] = useState('fps');
    const [hover, setHover] = useState(null);
    const metric = TREND_METRICS.find((item) => item.id === metricId);
    const hz = Math.max(...runs.map((run) => run.device?.hz || 60));
    const charts = useMemo(() => [
        ...SCENARIOS.map((scenario) => ({ id: scenario.id, title: scenario.label, read: (run, view) => valueOf(run, view, scenario.id, metricId) })),
        { id: 'load', title: 'Homes shown (s)', read: (run, view) => { const ms = run?.results?.[view]?.load?.homesMs; return Number.isFinite(ms) ? ms / 1000 : undefined; }, own: true },
    ], [metricId]);
    const maxOf = (chart) => {
        if (!chart.own && metricId === 'fps') return Math.ceil(hz / 10) * 10 + 10;
        if (!chart.own && metricId === 'smoothPct') return 100;
        const values = runs.flatMap((run) => VIEWS.map((view) => chart.read(run, view.id))).filter(Number.isFinite);
        const top = values.length ? Math.max(...values) : 1;
        return top > 0 ? top * 1.15 : 1;
    };
    const hovered = hover !== null ? runs[hover] : runs[runs.length - 1];
    return (
        <div className="ax-mpc">
            <div className="ax-mpc-trend-head">
                <div className="ax-segmented-buttons" role="group" aria-label="Measure">
                    {TREND_METRICS.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            className={item.id === metricId ? 'is-active' : ''}
                            aria-pressed={item.id === metricId}
                            title={item.hint}
                            onClick={() => setMetricId(item.id)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
                <span className="ax-muted">{metric.hint}</span>
            </div>
            {hovered && (
                <p className="ax-mpc-run-line">
                    <b>{hover !== null ? `Run ${hover + 1} of ${runs.length}` : 'Latest run'}</b>
                    {' · '}{formatDateTime(runTime(hovered))}
                    {' · '}<code>{shortCommit(hovered)}</code> {hovered.build?.message || ''}
                    {hovered.invalid ? ' · not comparable' : ''}
                </p>
            )}
            <div className="ax-mpc-grid">
                {charts.map((chart) => (
                    <SmallChart
                        key={chart.id}
                        title={chart.title}
                        runs={runs}
                        allRuns={allRuns}
                        read={chart.read}
                        metric={chart.own ? { id: 'homesS', unit: 's' } : metric}
                        hover={hover}
                        onHover={setHover}
                        max={maxOf(chart)}
                        ceiling={!chart.own && metricId === 'fps' ? hz : null}
                    />
                ))}
            </div>
            <div className="ax-mp-legend">
                {VIEWS.map((view) => <span key={view.id} className={`is-${view.id}`}>{view.label}</span>)}
                <span className="ax-mpc-legend-worse">clearly worse than the runs before it</span>
                {runs.length < 2 && <span className="ax-muted">Lines show from the second run.</span>}
            </div>
        </div>
    );
};
